import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import {
  getAllSupportTicket,
  getIssueTypeDropdownLists,
  updateBulkTickets,
  updateTicketStatus,
} from "store/actions/customerSupportAction"
import {
  getPerPageOptions,
  ticketListsStatus,
  todayDate,
} from "components/Common/constants"
import isPermissionEnabled from "common/isPermissionEnabled"
import Select from "components/Shared/TranslatedSelect"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import formatDateTime from "./../../components/Shared/formatDateTime"
import { setItemsPerPage } from "store/reducers/customerSupportReducer"

const TicketLists = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Support Tickets ")
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const issueTypeParam = searchParams.get("issueType")
  const flatpickrRef = useRef(null)
  const minDate = searchParams.get("minDate") || todayDate
  const maxDate = searchParams.get("maxDate") || todayDate

  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    currentPage,
    totalPages,
    itemsPerPage,
    ticketLists,
    issueTypeDropdownLists,
  } = useSelector(state => state.CustomerSupport)
  const [selectedId, setSelectedId] = useState(null)
  const [originalTicketStatus, setOriginalTicketStatus] = useState(null)
  const [selectedTicketStatus, setSelectedTicketStatus] = useState(null)
  const [remark, setRemark] = useState("")
  const [isStatusUpdated, setIsStatusUpdated] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [isShowCheckboxes, setIsShowCheckboxes] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "0"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    issueType: issueTypeParam
      ? issueTypeParam.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
  })

  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })
  const [selectedIds, setSelectedIds] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isModalOpen2, setIsModalOpen2] = useState(false)
  const toggleModal = () => {
    if (isModalOpen) {
      setSelectedStatus(null)
      setRemark("")
    }
    setIsModalOpen(!isModalOpen)
  }
  const toggleModal2 = () => {
    if (isModalOpen2) {
      setSelectedId(null)
      setSelectedTicketStatus(null)
      setOriginalTicketStatus(null)
      setRemark(null)
    }
    setIsModalOpen2(!isModalOpen2)
  }

  const { userRole, accountType } = useSelector(state => state.Login)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })
  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      17,
      "customer_support_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      17,
      "customer_support_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      17,
      "customer_support_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      17,
      "customer_support_4",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchTicketLists()
    fetchIssueTypeDropdownLists()
  }, [])

  const fetchTicketLists = () => {
    const { status, sortOrder, issueType } = selectedFilters
    const issueTypeIds = issueType?.map(item => item.value) || []

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      userId: "",
      issueType: issueTypeIds,
      keyword: searchTerm,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllSupportTicket(payload))
    setIsApiCalled(true)
  }

  const fetchIssueTypeDropdownLists = () => {
    dispatch(getIssueTypeDropdownLists())
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },

    {
      name: "status",
      type: "select",
      options: [
        {
          label: t("All"),
          value: 0,
        },
        {
          label: t("Pending"),
          value: 1,
        },
        {
          label: t("Open"),
          value: 2,
        },
        {
          label: t("In progress"),
          value: 3,
        },
        {
          label: t("Resolved"),
          value: 4,
        },
        {
          label: t("Closed"),
          value: 5,
        },
        {
          label: t("Rejected"),
          value: 6,
        },
      ],
      placeholder: t("Select Status"),
    },
    {
      name: "issueType",
      type: "multiSelect",
      options: [
        ...issueTypeDropdownLists.map(item => ({
          label: language === "sa" ? item.titleAr : item.title,
          value: item._id,
        })),
      ],
      placeholder: t("Select Support Issue Type"),
    },
  ]

  const handleFetchSupportTickets = (page = 1) => {
    // RESET SELECTED IDS
    setSelectedIds([])

    const { status, sortOrder, issueType } = selectedFilters
    const issueTypeIds = issueType?.map(item => item.value) || []
    if (status == 0 || status == 5 || status == 6) {
      setIsShowCheckboxes(false)
    } else {
      setIsShowCheckboxes(true)
    }
    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())
    if (issueType.length > 0) {
      const issueTypeString = issueType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("issueType", issueTypeString)
    }
    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      userId: "",
      issueType: issueTypeIds,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllSupportTicket(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/support-tickets?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchSupportTickets(1) // Always search from page 1
  }

  const onPageChange = page => {
    handleFetchSupportTickets(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleReset = () => {
    setDateRange({ min: todayDate, max: todayDate })
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate([todayDate, todayDate])
    }
    setSearchTerm("")
    setSelectedFilters({
      status: 0,
      sortOrder: 2,
      issueType: [],
    })
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 0,
      orderBy: 2,
      userId: "",
      issueType: [],
      keyword: "",
      dateRange: { min: todayDate, max: todayDate },
    }
    dispatch(getAllSupportTicket(payload))
    navigate(`/support-tickets?page=${1}`)

    // setDateRange({})
    // if (flatpickrRef.current) {
    //   flatpickrRef.current.flatpickr.clear()
    // }
  }

  const renderTicketStatus = actionType => {
    return ticketListsStatus[actionType] || ""
  }

  const handleStatusChange = () => {
    const payload = {
      id: selectedId,
      status: `${selectedTicketStatus}`,
      remark,
    }

    dispatch(updateTicketStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          toggleModal2()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const statusOptions = [
    { value: 1, label: t("Pending") },
    { value: 2, label: t("Open") },
    { value: 3, label: t("In progress") },
    { value: 4, label: t("Resolved") },
    { value: 5, label: t("Closed") },
    { value: 6, label: t("Rejected") },
  ]

  const perPageOptions = getPerPageOptions(totalCount)
  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  // BULK TICKET UPDATE
  // Handle individual row checkbox change
  const handleCheckboxClick = id => {
    setSelectedIds(prevIds =>
      prevIds.includes(id)
        ? prevIds.filter(selectedId => selectedId !== id)
        : [...prevIds, id]
    )
  }

  // Handle "Select All" checkbox change
  const handleSelectAllClick = () => {
    if (selectedIds.length === ticketLists.length) {
      setSelectedIds([]) // Unselect all
    } else {
      setSelectedIds(ticketLists.map(ticket => ticket._id)) // Select all
    }
  }

  const handleBulkTicketUpdate = () => {
    const payload = {
      ids: selectedIds,
      status: selectedStatus,
      remark,
    }

    dispatch(updateBulkTickets(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          toggleModal()
          handleFetchSupportTickets()
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const copyToClipboard = data => {
    const dataString = JSON.stringify(data, null, 2)

    const textarea = document.createElement("textarea")
    textarea.value = dataString
    textarea.setAttribute("readonly", "")
    textarea.style.position = "absolute"
    textarea.style.left = "-9999px"
    document.body.appendChild(textarea)

    try {
      textarea.select()
      const successful = document.execCommand("copy")
      if (successful) {
        dispatch(
          showAlert({ text: t("Data copied to clipboard!"), type: "success" })
        )
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "failed" }))
    } finally {
      document.body.removeChild(textarea)
    }
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Support Tickets")}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
                isAddBtn={
                  !loading &&
                  selectedFilters?.status !== 0 &&
                  selectedIds.length > 0
                }
                addBtnText={t("Update Bulk Tickets")}
                onAddBtnClick={toggleModal}
              />
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name, Phone Number, Ticket Number")}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  colSize={4}
                />
                <div className="table-responsive" style={{ minHeight: 340 }}>
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : ticketLists.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No support ticket lists found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0" hover>
                      <thead className="table-light align-baseline">
                        <tr>
                          <th>
                            {isShowCheckboxes && usersPermissions.edit && (
                              <input
                                type="checkbox"
                                className="mx-2 cursor-pointer"
                                onClick={handleSelectAllClick}
                                checked={
                                  selectedIds.length > 0 &&
                                  selectedIds.length === ticketLists.length
                                }
                              />
                            )}
                            {t("S. No.")}
                          </th>
                          <th style={{ maxWidth: 100, whiteSpace: "wrap" }}>
                            {t("Ticket Number")}
                          </th>
                          <th>{t("Name")}</th>
                          {/* <th>{t("Email")}</th> */}
                          <th>{t("Phone Number")}</th>
                          <th>{t("Issue Title")}</th>
                          <th>{t("Message")}</th>
                          <th>{t("Status")}</th>
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 150 }}>
                              {t("Update Status")}
                            </th>
                          )}
                          <th>{t("Created At")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticketLists?.map(
                          (
                            {
                              _id,
                              fullName,
                              email,
                              phoneNumber,
                              body,
                              status,
                              ticketId,
                              issueTypeInfo,
                              createdAt,
                            },
                            index
                          ) => (
                            <tr
                              key={index}
                              onClick={() =>
                                navigate(
                                  `/support-tickets/ticket-details?id=${_id}`
                                )
                              }
                              className="cursor-pointer"
                            >
                              <th
                                scope="row"
                                onClick={e => e.stopPropagation()}
                              >
                                {isShowCheckboxes && usersPermissions.edit && (
                                  <input
                                    type="checkbox"
                                    className="mx-2 cursor-pointer"
                                    onClick={e => {
                                      e.stopPropagation()
                                      handleCheckboxClick(_id)
                                    }}
                                    checked={selectedIds.includes(_id)}
                                  />
                                )}
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </th>
                              <td>{ticketId}</td>
                              <td>{fullName || "-"}</td>
                              {/* <td>{email || "-"}</td> */}
                              <td>
                                {phoneNumber ? (
                                  <LtrText>{phoneNumber}</LtrText>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>
                                {language === "sa"
                                  ? issueTypeInfo?.titleAr || "-"
                                  : issueTypeInfo?.title || "-"}
                              </td>
                              <td onClick={e => e.stopPropagation()}>
                                <p
                                  className="in_4_line"
                                  style={{ minWidth: 200 }}
                                >
                                  {body && (
                                    <i
                                      className="text-primary mdi mdi-content-copy cursor-pointer fw-bold mx-1"
                                      title={t("Copy")}
                                      onClick={() => copyToClipboard(body)}
                                    ></i>
                                  )}
                                  {body || "-"}
                                </p>
                              </td>
                              <td>{t(renderTicketStatus(status)) || "-"}</td>
                              {usersPermissions.edit && (
                                <td onClick={e => e.stopPropagation()}>
                                  <Button
                                    color="primary"
                                    onClick={() => {
                                      setSelectedId(_id)
                                      setSelectedTicketStatus(status)
                                      setOriginalTicketStatus(status)
                                      toggleModal2()
                                      setIsStatusUpdated(false)
                                    }}
                                    disabled={status === 5 || status === 6}
                                  >
                                    {t("Update Status")}
                                  </Button>
                                </td>
                              )}
                              <td>{formatDateTime(createdAt)}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && (
                  <div className="mt-3">
                    <Paginations
                      data={ticketLists}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal
        isOpen={isModalOpen}
        toggle={toggleModal}
        className=""
        centered
        size="md"
      >
        <ModalHeader toggle={toggleModal}>
          {t("Update Bulk Tickets")}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3 ">
            <label htmlFor="">{t("Select Status")}</label>
            <Select
              value={statusOptions.find(
                option => option.value == selectedStatus
              )}
              isDisabled={isLoading}
              onChange={selectedOption => {
                const { value } = selectedOption
                setSelectedStatus(value)
              }}
              options={statusOptions
                .filter(option => {
                  const optionValue = parseInt(option.value)
                  const currentStatus = parseInt(selectedFilters.status)

                  // If current status is Resolved (4), only show Resolved
                  if (currentStatus === 4) {
                    return optionValue === 5 // Closed
                  }

                  // If current status is Closed (5), exclude Rejected
                  if (currentStatus === 5) {
                    return optionValue !== 6 // Exclude Rejected
                  }

                  // For other statuses (1, 2, 3), show all including Rejected
                  if ([1, 2, 3].includes(currentStatus)) {
                    return true // Include all options
                  }

                  // Default fallback
                  return optionValue >= currentStatus
                })
                ?.map(option => ({
                  ...option,
                  isDisabled:
                    parseInt(option.value) <= parseInt(selectedFilters.status), // Disable current and all previous statuses
                }))}
              placeholder={t("Update Status")}
            />
          </div>
          <div className="mb-3">
            <div className="form_field">
              <label htmlFor="">{t("Remark(Optional)")}</label>
              <textarea
                placeholder={t("Remark(Optional)")}
                rows="4"
                onChange={e => setRemark(e.target.value)}
              />
            </div>
          </div>
          <div className="col-lg-12 mt-5 mb-3 d-flex align-items-center justify-content-end mt-2">
            <Button type="button" color="secondary" onClick={toggleModal}>
              {t("Cancel")}
            </Button>
            <Button
              className=" mx-2"
              color="success"
              type="submit"
              disabled={isLoading || !selectedStatus}
              onClick={handleBulkTicketUpdate}
            >
              {t("Update")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isModalOpen2}
        toggle={toggleModal2}
        className=""
        centered
        size="md"
      >
        <ModalHeader toggle={toggleModal2}>{t("Update Ticket")}</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg={12}>
              <div className="">
                <label htmlFor="">{t("Select Status")}</label>
                <Select
                  value={statusOptions.find(
                    option => option.value === selectedTicketStatus
                  )}
                  isDisabled={isLoading}
                  onChange={selectedOption => {
                    const { value } = selectedOption
                    setSelectedTicketStatus(value)
                    // handleStatusChange(_id, selectedOption.value)
                    // Mark as updated only if the new status is different
                    setIsStatusUpdated(value !== originalTicketStatus)
                  }}
                  options={statusOptions
                    .filter(option => {
                      const optionValue = parseInt(option.value)
                      const currentStatus = parseInt(originalTicketStatus)

                      // If current status is Resolved (4), only show Resolved
                      if (currentStatus === 4) {
                        return optionValue === 5
                      }

                      if (currentStatus === 5) {
                        return optionValue !== 6 // Exclude Rejected
                      }
                      // For other statuses (1, 2, 3), show all including Rejected
                      if ([1, 2, 3].includes(currentStatus)) {
                        return true // Include all options
                      }
                      // For other statuses, show the current status and higher options
                      return optionValue >= currentStatus
                    })
                    ?.map(option => ({
                      ...option,
                      isDisabled:
                        parseInt(option.value) <=
                        parseInt(originalTicketStatus),
                    }))}
                  placeholder={t("Update Status")}
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="form_field">
                <label htmlFor="">{t("Remark(Optional)")}</label>
                <textarea
                  placeholder={t("Remark(Optional)")}
                  rows="4"
                  onChange={e => setRemark(e.target.value)}
                />
              </div>
            </Col>
            <Col lg={12}>
              <div className="d-flex align-items-center justify-content-end mt-4">
                <Button type="button" color="secondary" onClick={toggleModal2}>
                  {t("Cancel")}
                </Button>
                <Button
                  className="mx-2"
                  color="success"
                  type="button"
                  disabled={!isStatusUpdated || isLoading}
                  onClick={handleStatusChange}
                >
                  {t("Update")}
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default TicketLists
